import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Zap, Clock, Search, MessageSquare, ArrowRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import TrustStrip from "@/components/site/TrustStrip";
import { HERO_VARIANTS, resolveVariant, captureAttribution, track } from "@/lib/analytics";

const POINTS = [
  { icon: Search, title: "Find the official line", body: "Verified phone numbers and links sourced straight from each brand's own website — with the date we last checked." },
  { icon: MessageSquare, title: "Describe it once", body: "Tell a specialist what's wrong in chat. Your first message is free — no forms, no phone tree." },
  { icon: Zap, title: "Get priority help", body: "Choose Urgent and a specialist jumps on your chat within 15 minutes — or your money back, automatically." },
];

export default function Landing() {
  const [variant, setVariant] = useState("a");

  useEffect(() => {
    const v = resolveVariant();
    setVariant(v);
    captureAttribution();
    track("landing_view", { variant: v });
  }, []);

  const hero = HERO_VARIANTS[variant] || HERO_VARIANTS.a;

  return (
    <div className="pb-16" data-testid="landing-page" data-variant={variant}>
      <Seo
        title="Get real customer support help, fast — It's Genie Assist"
        description="Stuck on hold? Describe your issue and a specialist helps you reach the right official support. Urgent help in 15 minutes or it's free."
      />

      {/* Hero */}
      <section className="border-b border-hair py-14 sm:py-20">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{hero.eyebrow}</p>
        <h1 className="mt-4 max-w-4xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-6xl" data-testid="landing-headline">
          {hero.headline}
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">
          {hero.sub}
        </p>
        <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <Link
            to="/chat"
            data-testid="landing-cta"
            className="inline-flex items-center justify-center gap-2 bg-accent px-7 py-4 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90"
          >
            {hero.cta} <ArrowRight className="h-4 w-4" />
          </Link>
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted">
            First message free · pay only when you&apos;re ready
          </span>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-muted">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-accent" /> Independent · never asks for passwords</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-accent" /> Urgent: 15 min or it&apos;s free</span>
        </div>
        <div className="mt-8">
          <TrustStrip />
        </div>
      </section>

      {/* How it works */}
      <section className="py-12">
        <div className="grid gap-px bg-hair sm:grid-cols-3">
          {POINTS.map((p) => (
            <div key={p.title} className="bg-paper p-6 sm:p-8">
              <p.icon className="h-7 w-7 text-accent" strokeWidth={1.75} />
              <h2 className="mt-4 font-serif text-xl font-medium text-ink">{p.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Guarantee + trust */}
      <section className="grid gap-px bg-hair md:grid-cols-2">
        <div className="bg-paper p-8">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-accent">
            <Zap className="h-4 w-4" /> Urgent guarantee
          </div>
          <p className="mt-3 font-serif text-2xl font-medium text-ink">15 minutes — or it&apos;s free.</p>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            Pick an Urgent session and if a specialist doesn&apos;t respond within 15 minutes, we
            refund you in full, automatically. No emails, no hoops.
          </p>
        </div>
        <div className="bg-paper p-8">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-accent">
            <ShieldCheck className="h-4 w-4" /> Safe by design
          </div>
          <p className="mt-3 font-serif text-2xl font-medium text-ink">We never ask for secrets.</p>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            We&apos;ll never ask for your passwords, full card numbers, or one-time codes. We&apos;re
            independent and not affiliated with any brand — we just help you reach the real ones.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-12 border-2 border-ink p-8 text-center sm:p-12" data-testid="landing-final-cta">
        <h2 className="font-serif text-3xl font-medium text-ink sm:text-4xl">Ready when you are.</h2>
        <p className="mx-auto mt-3 max-w-xl text-muted">
          Describe your issue and connect with a specialist in minutes. Standard $29 · Urgent $79 ·
          returning customers save 30%.
        </p>
        <Link
          to="/chat"
          data-testid="landing-cta-bottom"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-accent px-7 py-4 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90"
        >
          Start your chat <ArrowRight className="h-4 w-4" />
        </Link>
        <p className="mt-4 font-mono text-[11px] text-muted">
          By paying you agree to our{" "}
          <Link to="/terms" className="text-accent hover:underline">Terms</Link> and{" "}
          <Link to="/refunds" className="text-accent hover:underline">Refund Policy</Link>.
        </p>
      </section>
    </div>
  );
}
